// Stripe ACH typed in by hand (Oct 20, 2026).
//
// A homeowner who types their bank account and routing numbers into Stripe
// Checkout, instead of logging in to their bank, has to confirm a small test
// deposit before Stripe starts the debit. They get 10 days; then Stripe
// cancels the payment. The payment row stays at state "made" the whole time
// (so the pay buttons never ask for the money twice) and
// deal_payments.ach_verify_status = 'pending' says the homeowner still has to
// act. Written by stripe-webhook and stripe-payment-reconcile; read by the
// deal's Payments tab, the Finance board, the homeowner portal
// (ho_bank_verifications) and the invoice page (resolve_invoice_token).

export type MicrodepositType = "descriptor_code" | "amounts" | null | undefined;

/** "Tue, Oct 28". A date Stripe gives at midnight UTC is shown as that UTC
 *  day, so it can't slip back a day in Eastern time. */
export function fmtVerifyDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const midnightUtc = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: midnightUtc ? "UTC" : "America/New_York",
  });
}

/** "Wed, Oct 28, 12:15 PM" Eastern: when Stripe cancels the payment. Stored
 *  as checkout completed + 10 days; Stripe's own clock starts no earlier, so
 *  this is never later than the real cutoff. */
export function fmtVerifyDeadline(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
  });
}

function etDayIndex(ms: number): number {
  const ymd = new Date(ms).toLocaleDateString("en-CA", { timeZone: "America/New_York" }); // YYYY-MM-DD
  return Math.round(Date.parse(`${ymd}T00:00:00Z`) / 86_400_000);
}

/** Calendar days from today to that moment, in Eastern time (0 = today). */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : etDayIndex(t) - etDayIndex(Date.now());
}

export function daysLeftLabel(days: number | null): string | null {
  if (days == null) return null;
  if (days <= 0) return "due today";
  return days === 1 ? "due tomorrow" : `${days} days left`;
}

/** True once Stripe's expected arrival time for the test deposit has passed. */
export function depositShouldBeThere(arrivalIso: string | null | undefined): boolean {
  if (!arrivalIso) return false;
  const t = new Date(arrivalIso).getTime();
  return !Number.isNaN(t) && t <= Date.now();
}

/** What the homeowner looks for in their bank account, and what they enter. */
export function depositInstructions(type: MicrodepositType): { lookFor: string; enter: string } {
  return type === "amounts"
    ? {
        lookFor: "two small deposits from Stripe, each under $1",
        enter: "the two amounts",
      }
    : {
        lookFor: "a $0.01 deposit from Stripe. Its description shows a 6-character code that starts with SM",
        enter: "that code",
      };
}
